import { BibEmprestimoModel } from '@/models/biblioteca/bibemprestimo/bibemprestimo.model';

export type TipoComprovante = 'emprestimo' | 'renovacao' | 'devolucao';
export type PapelComprovante = '58' | '80' | 'A4';
export interface DadosComprovante {
  emprestimo: BibEmprestimoModel;
  usuario: string;
  biblioteca: string;
  titulo: string;
  autor: string;
  tombamento: string;
}

export const titulosComprovante: Record<TipoComprovante, string> = {
  emprestimo: 'Empréstimo',
  renovacao: 'Renovação',
  devolucao: 'Devolução',
};

function escapar(valor: unknown): string {
  return String(valor ?? '').replace(
    /[&<>"']/g,
    (letra) =>
      (
        ({
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#39;',
        }) as Record<string, string>
      )[letra] || letra,
  );
}

function data(valor: Date | string | undefined): string {
  if (!valor) return '—';
  const texto = valor instanceof Date ? valor.toISOString() : valor;
  const partes = /^(\d{4})-(\d{2})-(\d{2})/.exec(texto);
  return partes ? `${partes[3]}/${partes[2]}/${partes[1]}` : '—';
}

/** Relatórios imprimíveis de uma operação já gravada, sem depender de fontes ou imagens externas. */
export function montarComprovante(
  dados: DadosComprovante,
  tipo: TipoComprovante,
  papel: PapelComprovante,
): string {
  const e = dados.emprestimo;
  if (!e.bibEmprestimoId) throw new Error('Salve o empréstimo antes de emitir o comprovante.');
  if (tipo === 'devolucao' && (Number(e.devolvido) !== 1 || !e.dataDevolucao)) {
    throw new Error('A devolução ainda não foi registrada.');
  }
  if (tipo === 'renovacao' && !Number(e.renovacoes))
    throw new Error('A renovação ainda não foi registrada.');
  const linha = (rotulo: string, valor: unknown) =>
    `<p><strong>${rotulo}:</strong> ${escapar(valor || '—')}</p>`;
  const detalhes =
    tipo === 'devolucao'
      ? linha('Devolução', data(e.dataDevolucao)) +
        linha(
          'Multa',
          Number(e.multa || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
        ) +
        linha(
          'Situação da multa',
          Number(e.dispensaMulta) === 1
            ? 'Dispensada'
            : Number(e.multaPaga) === 1
              ? 'Paga'
              : Number(e.multa) > 0
                ? 'Pendente'
                : 'Sem multa',
        )
      : linha(tipo === 'renovacao' ? 'Renovação' : 'Empréstimo', data(e.dataEmprestimo)) +
        linha('Devolver até', data(e.dataVencimento));
  return `<!doctype html><html lang="pt-BR"><head><meta charset="utf-8">
<title>Comprovante de ${titulosComprovante[tipo]} — ${e.bibEmprestimoId}</title>
<style id="papel">@page { size: ${papel !== 'A4' ? `${papel}mm 200mm` : 'A4'}; margin: ${papel !== 'A4' ? '0' : '12mm'}; }</style>
<style>*{box-sizing:border-box}body{margin:0;background:white;color:black;font:12px Arial,sans-serif}
.recibo{width:${papel !== 'A4' ? `${papel}mm` : '100%'};max-width:${papel !== 'A4' ? `${papel}mm` : '180mm'};padding:4mm;margin:0 auto;overflow-wrap:anywhere}
h1{font-size:15px;text-align:center;margin:0 0 4mm}h2{font-size:13px;text-align:center;margin:3mm 0}p{margin:2mm 0;line-height:1.4}
hr{border:0;border-top:1px dashed black;margin:3mm 0}.assinatura{margin-top:10mm;border-top:1px solid black;text-align:center;padding-top:2mm;font-size:11px}
.rodape{font-size:10px;text-align:center;margin-top:4mm}@media print{body{background:white}.recibo{margin:0}}</style>
</head><body><article class="recibo"><h1>${escapar(dados.biblioteca)}</h1>
<h2>Comprovante de ${titulosComprovante[tipo]}</h2><hr>
${linha('Registro', e.bibEmprestimoId)}${linha('Usuário', dados.usuario)}${linha('Código do usuário', e.segUsuarioId)}
<hr>${linha('Documento', dados.titulo)}${linha('Autor', dados.autor)}${linha('Tombamento', dados.tombamento)}
<hr>${detalhes}
<p>${tipo === 'devolucao' ? 'Devolução registrada na biblioteca.' : 'Conserve este comprovante e devolva o exemplar até a data indicada.'}</p>
<div class="assinatura">${tipo === 'devolucao' ? 'Responsável pelo recebimento' : 'Usuário / responsável'}</div>
<p class="rodape">Comprovante da biblioteca — sem valor fiscal.</p></article></body></html>`;
}
